/**
 * @fileoverview Real-time WebSocket Client for the Fan Mobile View (/cronometro) in the "Cronómetro" dynamic (Tile ID 50).
 * Connects via SignalRService native WebSockets with role 'cronometer:fan'.
 * Handles hot session rehydration from localStorage ('cronometer_user') on connect/reconnect,
 * event subscription for gameplay phases and stop results, and typed emission methods for player actions.
 * 
 * @module utilities/WS/CronometerFanWS
 */

import SignalRService from './SignalRService';
import {
    GetSession,
    SaveSession,
    UpdateSessionPhases,
    TrackDynamicEvent
} from '../BL/Cronometer';

// =============================================================================
// INTERNAL HANDLER REFERENCES (To prevent memory leaks and duplicates)
// =============================================================================

let registeredMessageHandler = null;
let registeredConnectedHandler = null;
let registeredReconnectedHandler = null;
let registeredReconnectingHandler = null;
let registeredCloseHandler = null;
let registeredErrorHandler = null;

// Individual event listeners map
const eventCallbacks = {
    crono_game_state: [],
    crono_change_phase: [],
    crono_start_phase: [],
    crono_stop_result: [],
    crono_player_eliminated: [],
    crono_end_phase: [],
    crono_reset_game: []
};

/**
 * Rehydrates session from localStorage ('cronometer_user') and registers player with the backend.
 * Enables zero-friction hot reconnection during live stadium matches.
 * 
 * @function RehydrateFanSession
 */
export const RehydrateFanSession = () => {
    try {
        const session = GetSession();
        if (session && session.participantId) {
            const rehydratePayload = {
                type: 'crono_register_player',
                participantId: Number(session.participantId),
                name: session.name || '',
                email: session.email || '',
                phone: session.phone || '',
                abonado: session.isAbonado ? 'Sí' : 'No'
            };

            SignalRService.send(rehydratePayload);
        }
    } catch (err) {
        // Ignore session rehydration failures
    }
};

/**
 * Subscribes a callback to a specific Cronometer event.
 * 
 * @function OnCronometerEvent
 * @param {string} eventName - One of 'crono_game_state', 'crono_start_phase', 'crono_stop_result', 'crono_player_eliminated', 'crono_end_phase', 'crono_reset_game'.
 * @param {Function} callback - Event listener function.
 */
export const OnCronometerEvent = (eventName, callback) => {
    if (eventCallbacks[eventName] && typeof callback === 'function') {
        eventCallbacks[eventName].push(callback);
    }
};

/**
 * Unsubscribes a callback from a specific Cronometer event.
 * 
 * @function OffCronometerEvent
 * @param {string} eventName
 * @param {Function} callback
 */
export const OffCronometerEvent = (eventName, callback) => {
    if (eventCallbacks[eventName]) {
        if (!callback) {
            eventCallbacks[eventName] = [];
        } else {
            eventCallbacks[eventName] = eventCallbacks[eventName].filter(cb => cb !== callback);
        }
    }
};

/**
 * Internal helper to trigger registered event callbacks.
 */
const notifyEventCallbacks = (eventName, data) => {
    if (eventCallbacks[eventName]) {
        eventCallbacks[eventName].forEach(cb => {
            try {
                cb(data);
            } catch (e) {
                // Ignore listener errors
            }
        });
    }
};

/**
 * Establishes a persistent real-time WebSocket connection for the fan's mobile device.
 * 
 * @function ConnectWebSocketForCronometerFan
 * @param {Function} [onIncomingMessage=()=>{}] - General callback for all parsed inbound envelopes.
 * @param {Function} [onStatusChange=()=>{}] - Callback for connection status ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @returns {Promise<void>}
 */
export const ConnectWebSocketForCronometerFan = async (
    onIncomingMessage = () => {},
    onStatusChange = () => {}
) => {
    onStatusChange('connecting');

    // Clean up existing handlers before registering new ones
    if (registeredMessageHandler) SignalRService.off('message', registeredMessageHandler);
    if (registeredConnectedHandler) SignalRService.off('connected', registeredConnectedHandler);
    if (registeredReconnectedHandler) SignalRService.off('reconnected', registeredReconnectedHandler);
    if (registeredReconnectingHandler) SignalRService.off('reconnecting', registeredReconnectingHandler);
    if (registeredCloseHandler) SignalRService.off('close', registeredCloseHandler);
    if (registeredErrorHandler) SignalRService.off('error', registeredErrorHandler);

    // 1. Inbound message dispatcher
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const eventType = (messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
        const payload = messageEnvelope.payload !== undefined
            ? messageEnvelope.payload
            : (messageEnvelope.data !== undefined ? messageEnvelope.data : messageEnvelope);

        // Process known Cronometer fan events
        switch (eventType) {
            case 'crono_game_state':
                // Sincronizar estado global del juego
                if (payload.participant && payload.participant.participantId) {
                    SaveSession({
                        participantId: payload.participant.participantId,
                        name: payload.participant.name,
                        isAbonado: payload.participant.isAbonado,
                        wonPhases: payload.participant.wonPhases || {}
                    });
                }
                notifyEventCallbacks('crono_game_state', payload);
                break;

            case 'crono_change_phase':
                notifyEventCallbacks('crono_change_phase', payload);
                break;

            case 'crono_start_phase':
                // Inicio de fase y cuenta regresiva
                TrackDynamicEvent('game_start', {
                    fase: payload.fase,
                    z: payload.z,
                    margin: payload.margin,
                    intermitente: payload.intermitente
                });
                notifyEventCallbacks('crono_start_phase', payload);
                break;

            case 'crono_stop_result':
                // Resultado evaluado por el backend en RAM
                UpdateSessionPhases(payload.phase, payload.isClassified, payload.time);
                TrackDynamicEvent(payload.isClassified ? 'classified' : 'eliminated', {
                    phase: payload.phase,
                    time: payload.time,
                    diff: payload.diff,
                    status: payload.status
                });
                notifyEventCallbacks('crono_stop_result', payload);
                break;

            case 'crono_player_eliminated':
                // Jugador eliminado por omisión, trampa o fase no clasificada
                if (payload.phase) {
                    UpdateSessionPhases(payload.phase, false, 0);
                }
                TrackDynamicEvent('eliminated', {
                    phase: payload.phase,
                    reason: payload.reason
                });
                notifyEventCallbacks('crono_player_eliminated', payload);
                break;

            case 'crono_end_phase':
                // Fin de fase y balance de clasificados
                notifyEventCallbacks('crono_end_phase', payload);
                break;

            case 'crono_reset_game':
                // Reinicio global de la dinámica
                SaveSession({
                    wonPhases: {},
                    phaseReached: 1,
                    isWinner: false,
                    lastTimeAchieved: 0
                });
                notifyEventCallbacks('crono_reset_game', payload);
                break;

            default:
                break;
        }

        // Notify general inbound message handler
        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    // 2. Lifecycle Handlers
    registeredConnectedHandler = () => {
        onStatusChange('connected');
        // Auto-rehydrate session if player was already registered
        RehydrateFanSession();
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
        // Auto-rehydrate session on reconnection
        RehydrateFanSession();
    };

    registeredReconnectingHandler = (info) => {
        onStatusChange('reconnecting', info);
    };

    registeredCloseHandler = (closeEvent) => {
        onStatusChange('closed', closeEvent);
    };

    registeredErrorHandler = (err) => {
        onStatusChange('error', err);
    };

    // Attach listeners to SignalRService
    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('connected', registeredConnectedHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);
    SignalRService.on('error', registeredErrorHandler);

    // Connect to native WebSocket via SignalR adapter
    try {
        await SignalRService.connect({ tipo: 'cronometer:fan' });
    } catch (connectionError) {
        onStatusChange('error', connectionError);
    }
};

/**
 * Sends the exact time in seconds when the player stopped their stopwatch.
 * Evaluated authoritatively in memory by the backend.
 * 
 * @function SendPlayerStopped
 * @param {number|string} timeInSeconds - Time achieved by the participant.
 * @returns {boolean} True if sent successfully.
 */
export const SendPlayerStopped = (timeInSeconds) => {
    try {
        const roundedTime = Math.round(Number(timeInSeconds) * 100) / 100;
        const payload = {
            type: 'crono_player_stopped',
            time: roundedTime
        };

        const sent = SignalRService.send(payload);

        if (sent) {
            TrackDynamicEvent('game_stopped', { time: roundedTime });
        }

        return sent;
    } catch (err) {
        return false;
    }
};

/**
 * Registers or re-associates a player with the WebSocket connection in real time.
 * 
 * @function SendRegisterPlayer
 * @param {Object} playerData - Registration attributes.
 * @param {number} [playerData.participantId] - Existing participant ID from database.
 * @param {string} playerData.name - Full name.
 * @param {string} playerData.email - Lowercase email.
 * @param {string} playerData.phone - 10-digit phone number.
 * @param {string} [playerData.abonado] - 'Sí' or 'No'.
 * @param {boolean} [playerData.acceptedTerms] - Consent flag.
 * @param {boolean} [playerData.acceptedMarketing] - Marketing consent flag.
 * @param {string} [playerData.trafficSource] - Origin source.
 * @returns {boolean} True if sent successfully.
 */
export const SendRegisterPlayer = (playerData) => {
    try {
        if (!playerData || typeof playerData !== 'object') return false;

        const payload = {
            type: 'crono_register_player',
            participantId: playerData.participantId ? Number(playerData.participantId) : null,
            name: playerData.name || '',
            email: (playerData.email || '').toLowerCase().trim(),
            phone: playerData.phone || '',
            abonado: playerData.abonado || (playerData.isAbonado ? 'Sí' : 'No'),
            acceptedTerms: Boolean(playerData.acceptedTerms !== false),
            acceptedMarketing: Boolean(playerData.acceptedMarketing),
            trafficSource: playerData.trafficSource || 'qr_estadio'
        };

        const sent = SignalRService.send(payload);

        if (sent && playerData.participantId) {
            SaveSession({
                participantId: playerData.participantId,
                name: payload.name,
                email: payload.email,
                phone: payload.phone,
                isAbonado: payload.abonado === 'Sí'
            });
        }

        return sent;
    } catch (err) {
        return false;
    }
};

/**
 * Disconnects the fan WebSocket client and unbinds event listeners.
 * 
 * @function DisconnectWebSocketForCronometerFan
 * @returns {Promise<void>}
 */
export const DisconnectWebSocketForCronometerFan = async () => {
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
        registeredMessageHandler = null;
    }
    if (registeredConnectedHandler) {
        SignalRService.off('connected', registeredConnectedHandler);
        registeredConnectedHandler = null;
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
        registeredReconnectedHandler = null;
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
        registeredReconnectingHandler = null;
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
        registeredCloseHandler = null;
    }
    if (registeredErrorHandler) {
        SignalRService.off('error', registeredErrorHandler);
        registeredErrorHandler = null;
    }

    // Reset callbacks
    Object.keys(eventCallbacks).forEach(key => {
        eventCallbacks[key] = [];
    });

    try {
        await SignalRService.disconnect();
    } catch (e) {
        // Ignore disconnect error
    }
};

export default {
    ConnectWebSocketForCronometerFan,
    DisconnectWebSocketForCronometerFan,
    SendPlayerStopped,
    SendRegisterPlayer,
    RehydrateFanSession,
    OnCronometerEvent,
    OffCronometerEvent
};
